import React, { useCallback, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import {
  FaArrowDown,
  FaArrowUp,
  FaChevronLeft,
  FaChevronRight,
  FaExclamationCircle,
  FaInbox,
  FaRedo,
  FaTimes,
} from "react-icons/fa";
import useApi from "../../../hooks/useApi";
import { STERLING_HISTORY_URL } from "../../../api/urls";
import {
  HistoryContainer,
  HistoryEmptyIcon,
  HistoryEmptyText,
  HistoryEmptyWrap,
  HistoryErrorText,
  HistoryErrorWrap,
  HistoryLoadingWrap,
  HistoryPageBtn,
  HistoryPageInfo,
  HistoryPaginationRow,
  HistoryRetryBtn,
  HistorySkeleton,
  TxnAmountCol,
  TxnAmount,
  TxnBalance,
  TxnCard,
  TxnChannel,
  TxnDateGroup,
  TxnDateLabel,
  TxnDetailLabel,
  TxnDetailRow,
  TxnDetailSection,
  TxnDetailSectionTitle,
  TxnDetailValue,
  TxnDrawerAmountBanner,
  TxnDrawerAmountLabel,
  TxnDrawerAmountType,
  TxnDrawerBigAmount,
  TxnDrawerBody,
  TxnDrawerClose,
  TxnDrawerHandle,
  TxnDrawerHeader,
  TxnDrawerOverlay,
  TxnDrawerSheet,
  TxnDrawerTitle,
  TxnDrawerStatusRow,
  TxnIconWrap,
  TxnMeta,
  TxnNarration,
  TxnStatusBadge,
  TxnSubRow,
  TxnTime,
  TxnRef,
} from "./style";

const PAGE_SIZE = 20;

const fmt = (amount) =>
  `₦${Number(amount ?? 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
};

const fmtTime = (iso) => {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-NG", { hour: "2-digit", minute: "2-digit" });
};

const fmtDateTime = (iso) => `${fmtDate(iso)}, ${fmtTime(iso)}`;

const statusLabel = (s = "") => {
  const lower = s.toLowerCase();
  if (lower === "successful" || lower === "success") return "Successful";
  if (lower === "pending")    return "Pending";
  if (lower === "initiated")  return "Initiated";
  if (lower === "processing") return "Processing";
  if (lower === "failed")     return "Failed";
  return s || "Unknown";
};

// Group transactions by calendar date
const groupByDate = (txns) => {
  const map = new Map();
  txns.forEach((t) => {
    const key = fmtDate(t.date || t.createdAt || t.transactionDate);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(t);
  });
  return [...map.entries()];
};

// ── Detail drawer (portal) ────────────────────────────────────────────────────
const TxnDetailDrawer = ({ txn, onClose }) => {
  if (!txn) return null;
  const type = (txn.type  || "debit").toLowerCase();
  const isCredit = type === "credit";
  const amount = txn.amount;
  const status = (txn.status || "").toLowerCase();

  return ReactDOM.createPortal(
    <TxnDrawerOverlay onClick={onClose}>
      <TxnDrawerSheet onClick={(e) => e.stopPropagation()}>
        <TxnDrawerHandle />

        <TxnDrawerHeader>
          <TxnDrawerTitle>Transaction Details</TxnDrawerTitle>
          <TxnDrawerClose onClick={onClose}>
            <FaTimes size={12} />
          </TxnDrawerClose>
        </TxnDrawerHeader>

        <TxnDrawerAmountBanner>
          <TxnDrawerAmountType $type={isCredit ? "credit" : "debit"}>
            {isCredit ? <FaArrowDown size={18} /> : <FaArrowUp size={18} />}
          </TxnDrawerAmountType>
          <TxnDrawerBigAmount $type={isCredit ? "credit" : "debit"}>
            {isCredit ? "+" : "-"}{fmt(amount)}
          </TxnDrawerBigAmount>
          <TxnDrawerAmountLabel>
            {isCredit ? "Money Received" : "Money Sent"}
          </TxnDrawerAmountLabel>
          <TxnDrawerStatusRow>
            <TxnStatusBadge $status={status}>{statusLabel(status)}</TxnStatusBadge>
          </TxnDrawerStatusRow>
        </TxnDrawerAmountBanner>

        <TxnDrawerBody>
          {/* Transaction info */}
          <TxnDetailSection>
            <TxnDetailSectionTitle>Transaction Info</TxnDetailSectionTitle>
            {(txn.narration || txn.description) && (
              <TxnDetailRow>
                <TxnDetailLabel>Narration</TxnDetailLabel>
                <TxnDetailValue>{txn.narration || txn.description}</TxnDetailValue>
              </TxnDetailRow>
            )}
            <TxnDetailRow>
              <TxnDetailLabel>Date &amp; Time</TxnDetailLabel>
              <TxnDetailValue>{fmtDateTime(txn.date || txn.createdAt || txn.transactionDate)}</TxnDetailValue>
            </TxnDetailRow>
            {(txn.channel) && (
              <TxnDetailRow>
                <TxnDetailLabel>Channel</TxnDetailLabel>
                <TxnDetailValue>{txn.channel}</TxnDetailValue>
              </TxnDetailRow>
            )}
            {(txn.fromAccount) && (
              <TxnDetailRow>
                <TxnDetailLabel>From Account</TxnDetailLabel>
                <TxnDetailValue>{txn.fromAccount}</TxnDetailValue>
              </TxnDetailRow>
            )}
            {(txn.toAccount) && (
              <TxnDetailRow>
                <TxnDetailLabel>To Account</TxnDetailLabel>
                <TxnDetailValue>{txn.toAccount}</TxnDetailValue>
              </TxnDetailRow>
            )}
            {(txn.reference) && (
              <TxnDetailRow>
                <TxnDetailLabel>Reference</TxnDetailLabel>
                <TxnDetailValue>{txn.reference}</TxnDetailValue>
              </TxnDetailRow>
            )}
          </TxnDetailSection>

          {/* Counterparty */}
          {(txn.senderName || txn.beneficiaryName) && (
            <TxnDetailSection>
              <TxnDetailSectionTitle>
                {isCredit ? "Sender Details" : "Recipient Details"}
              </TxnDetailSectionTitle>
              {(txn.senderName || txn.beneficiaryName) && (
                <TxnDetailRow>
                  <TxnDetailLabel>Name</TxnDetailLabel>
                  <TxnDetailValue>
                    { txn.senderName || txn.beneficiaryName}
                  </TxnDetailValue>
                </TxnDetailRow>
              )}
              {(txn.counterpartyAccount || txn.beneficiaryAccount) && (
                <TxnDetailRow>
                  <TxnDetailLabel>Account No.</TxnDetailLabel>
                  <TxnDetailValue>{txn.counterpartyAccount || txn.beneficiaryAccount}</TxnDetailValue>
                </TxnDetailRow>
              )}
              {(txn.counterpartyBank || txn.beneficiaryBank) && (
                <TxnDetailRow>
                  <TxnDetailLabel>Bank</TxnDetailLabel>
                  <TxnDetailValue>{txn.counterpartyBank || txn.beneficiaryBank}</TxnDetailValue>
                </TxnDetailRow>
              )}
            </TxnDetailSection>
          )}

          {/* Balance */}
          {(txn.old_balance != null || txn.new_balance != null ||
            txn.openingBalance != null || txn.closingBalance != null) && (
            <TxnDetailSection>
              <TxnDetailSectionTitle>Balance Impact</TxnDetailSectionTitle>
              {(txn.old_balance != null || txn.openingBalance != null) && (
                <TxnDetailRow>
                  <TxnDetailLabel>Previous Balance</TxnDetailLabel>
                  <TxnDetailValue>{fmt(txn.old_balance ?? txn.openingBalance)}</TxnDetailValue>
                </TxnDetailRow>
              )}
              <TxnDetailRow>
                <TxnDetailLabel>{isCredit ? "Amount Credited" : "Amount Debited"}</TxnDetailLabel>
                <TxnDetailValue style={{ color: isCredit ? "#059669" : "#c81e34" }}>
                  {isCredit ? "+" : "-"}{fmt(amount)}
                </TxnDetailValue>
              </TxnDetailRow>
              {(txn.new_balance != null || txn.closingBalance != null) && (
                <TxnDetailRow>
                  <TxnDetailLabel>New Balance</TxnDetailLabel>
                  <TxnDetailValue style={{ fontFamily: "rebondG-Bold" }}>
                    {fmt(txn.new_balance ?? txn.closingBalance)}
                  </TxnDetailValue>
                </TxnDetailRow>
              )}
            </TxnDetailSection>
          )}
        </TxnDrawerBody>
      </TxnDrawerSheet>
    </TxnDrawerOverlay>,
    document.body
  );
};

// ── Main history tab ──────────────────────────────────────────────────────────
export const SterlingHistoryTab = () => {
  const api = useApi();
  const [status, setStatus] = useState("loading"); // loading | error | empty | done
  const [transactions, setTransactions] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [selected, setSelected] = useState(null);

  const load = useCallback(
    (p = 1) => {
      setStatus("loading");
      setErrorMsg(null);
      api
        .get(STERLING_HISTORY_URL, { params: { page: p, pageSize: PAGE_SIZE } })
        .then((res) => {
          const data = res.data;
          const txns = data?.data || [];
          const pagination = data?.pagination || data?.meta || {};
          setTransactions(txns);
          setTotalPages(
            pagination.totalPages ||
              Math.ceil((pagination.totalItems || pagination.total || txns.length) / PAGE_SIZE) ||
              1
          );
          setTotalItems(pagination.totalItems || pagination.total || txns.length);
          setStatus(txns.length ? "done" : "empty");
        })
        .catch((err) => {
          setErrorMsg(err?.response?.data?.error || err?.message || "Failed to load history");
          setStatus("error");
        });
    },
    [api]
  );

  useEffect(() => {
    load(page);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const grouped = status === "done" ? groupByDate(transactions) : [];

  return (
    <HistoryContainer>
      {/* Loading skeletons */}
      {status === "loading" && (
        <HistoryLoadingWrap>
          {[72, 56, 72, 56, 72].map((h, i) => (
            <HistorySkeleton key={i} $h={`${h}px`} />
          ))}
        </HistoryLoadingWrap>
      )}

      {/* Error */}
      {status === "error" && (
        <HistoryErrorWrap>
          <FaExclamationCircle size={28} color="#c81e34" />
          <HistoryErrorText>{errorMsg}</HistoryErrorText>
          <HistoryRetryBtn onClick={() => load(page)}>
            <FaRedo size={11} /> Retry
          </HistoryRetryBtn>
        </HistoryErrorWrap>
      )}

      {/* Empty */}
      {status === "empty" && (
        <HistoryEmptyWrap>
          <HistoryEmptyIcon><FaInbox /></HistoryEmptyIcon>
          <HistoryEmptyText>No transactions found</HistoryEmptyText>
        </HistoryEmptyWrap>
      )}

      {/* Transaction list */}
      {status === "done" && (
        <>
          {grouped.map(([date, txns]) => (
            <TxnDateGroup key={date}>
              <TxnDateLabel>{date}</TxnDateLabel>
              {txns.map((t, i) => {
                const type = (t.ttype  || "debit").toLowerCase();
                const isCredit = type === "credit";
                const amount = t.amount;
                const balance = t.new_balance;
                const narration = t.description || (isCredit ? "Inflow" : "Transfer");
                const channel = t.channel  || "Wallet";
                const status = (t.status || "status").toLowerCase();
                const time = fmtTime(t.createdAt);
                const ref = t.reference;
                const counterparty = isCredit
                  ? (t.senderName )
                  : (t.beneficiaryName);

                return (
                  <TxnCard key={t.id || t._id || i} onClick={() => setSelected(t)}>
                    <TxnIconWrap $type={isCredit ? "credit" : "debit"}>
                      {isCredit ? <FaArrowDown size={14} /> : <FaArrowUp size={14} />}
                    </TxnIconWrap>

                    <TxnMeta>
                      <TxnNarration>{narration}</TxnNarration>
                      {counterparty && (
                        <TxnRef style={{ color: "#475569", fontFamily: "rebondG-Semi-Bold", fontSize: "11px", marginBottom: "2px" }}>
                          {isCredit ? "From" : "To"}: {counterparty}
                        </TxnRef>
                      )}
                      <TxnSubRow>
                        <TxnTime>{time}</TxnTime>
                        {channel && <TxnChannel>{channel}</TxnChannel>}
                        {status && (
                          <TxnStatusBadge $status={status}>
                            {statusLabel(status)}
                          </TxnStatusBadge>
                        )}
                      </TxnSubRow>
                      {ref && <TxnRef>Ref: {ref}</TxnRef>}
                    </TxnMeta>

                    <TxnAmountCol>
                      <TxnAmount $type={isCredit ? "credit" : "debit"}>
                        {isCredit ? "+" : "-"}{fmt(amount)}
                      </TxnAmount>
                      {balance != null && (
                        <TxnBalance>Bal: {fmt(balance)}</TxnBalance>
                      )}
                    </TxnAmountCol>
                  </TxnCard>
                );
              })}
            </TxnDateGroup>
          ))}

          {totalPages > 1 && (
            <HistoryPaginationRow>
              <HistoryPageBtn onClick={() => setPage((p) => p - 1)} disabled={page <= 1}>
                <FaChevronLeft size={11} /> Prev
              </HistoryPageBtn>
              <HistoryPageInfo>
                Page {page} of {totalPages} · {totalItems} transactions
              </HistoryPageInfo>
              <HistoryPageBtn onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}>
                Next <FaChevronRight size={11} />
              </HistoryPageBtn>
            </HistoryPaginationRow>
          )}
        </>
      )}

      {/* Detail drawer */}
      <TxnDetailDrawer txn={selected} onClose={() => setSelected(null)} />
    </HistoryContainer>
  );
};
